import type { SVGProps } from 'react';

/** Icons lifted from the design's inline SVGs. */
type P = SVGProps<SVGSVGElement> & { size?: number };

const base = (size: number, p: P) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  ...p,
});

export const ChevronLeft = ({ size = 14, ...p }: P) => (
  <svg
    width={(size * 8) / 14}
    height={size}
    viewBox="0 0 8 14"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...p}
  >
    <path d="M7 1L1 7l6 6" />
  </svg>
);

export const ChevronRight = ({ size = 14, color = '#cbd5e1', ...p }: P & { color?: string }) => (
  <svg
    width={(size * 8) / 14}
    height={size}
    viewBox="0 0 8 14"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...p}
  >
    <path d="M1 1l6 6-6 6" />
  </svg>
);

export const ChevronDown = ({ size = 11, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={2.6}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export const PlusIcon = ({ size = 18, strokeWidth = 2.6, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={strokeWidth}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const CheckIcon = ({ size = 22, strokeWidth = 2.4, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={strokeWidth}>
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

export const SearchIcon = ({ size = 16, ...p }: P) => (
  <svg {...base(size, p)} stroke="#94a3b8" strokeWidth={2}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" />
  </svg>
);

export const ScanIcon = ({ size = 17, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={2}>
    <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
    <path d="M3 12h18" />
  </svg>
);

export const QrScanIcon = ({ size = 18, ...p }: P) => (
  <svg {...base(size, p)} stroke="#0369a1" strokeWidth={2}>
    <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
    <rect x="7.5" y="7.5" width="9" height="9" rx="1.5" fill="#0369a1" fillOpacity=".15" />
  </svg>
);

export const UserPlusIcon = ({ size = 22, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={2.2}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M19 8v6M22 11h-6" />
  </svg>
);

export const GroupsIcon = ({ size = 22, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={2}>
    <circle cx="9" cy="8" r="3.5" fill="currentColor" fillOpacity=".15" />
    <path d="M2.75 20a6.25 6.25 0 0 1 12.5 0z" fill="currentColor" fillOpacity=".15" />
    <path d="M15.5 4.75a3.5 3.5 0 0 1 0 6.5M18 14.5a6.25 6.25 0 0 1 3.25 5.5" />
  </svg>
);

export const GroupRowIcon = ({ size = 20, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={2}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
  </svg>
);

export const ExcelFileIcon = ({ size = 16, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={1.9}>
    <path
      d="M5 2.75h9l5 5V20a1.25 1.25 0 0 1-1.25 1.25H5A1.25 1.25 0 0 1 3.75 20V4A1.25 1.25 0 0 1 5 2.75z"
      fill="currentColor"
      fillOpacity=".16"
    />
    <path d="M13.75 2.75v5h5" />
    <path d="M11.5 11v6.5M8.75 14.75 11.5 17.5l2.75-2.75" />
  </svg>
);

export const DownloadIcon = ({ size = 17, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={2.2}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
  </svg>
);

export const ShieldTeamIcon = ({ size = 20, ...p }: P) => (
  <svg {...base(size, p)} stroke="#6d28d9" strokeWidth={1.7}>
    <path
      d="M12 2.75 4.75 5.5v5.25c0 4.6 3.1 8.6 7.25 10 4.15-1.4 7.25-5.4 7.25-10V5.5z"
      fill="#6d28d9"
      fillOpacity=".14"
    />
    <circle cx="12" cy="10" r="2.5" />
    <path d="M8 16a4.5 4.5 0 0 1 8 0" />
  </svg>
);

export const SheetIcon = ({ size = 20 }: P) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M6 2.75h8.5l4.75 4.75V20A1.25 1.25 0 0 1 18 21.25H6A1.25 1.25 0 0 1 4.75 20V4A1.25 1.25 0 0 1 6 2.75z"
      fill="#16a34a"
      fillOpacity=".14"
      stroke="#15803d"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path d="M14.25 2.75V7.5h5" stroke="#15803d" strokeWidth="1.7" strokeLinejoin="round" />
    <path
      d="M8 11.5h8M8 14.75h8M8 18h8M11.5 11.5V18"
      stroke="#15803d"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export const IdCardIcon = ({ size = 20, ...p }: P) => (
  <svg {...base(size, p)} stroke="#0369a1" strokeWidth={1.7}>
    <rect x="2.75" y="4.75" width="18.5" height="14.5" rx="2.5" fill="#0369a1" fillOpacity=".14" />
    <rect x="5.75" y="8" width="5" height="6.5" rx="1.2" fill="#0369a1" fillOpacity=".35" />
    <path d="M13.5 9h4.75M13.5 12h4.75M13.5 15h3" />
  </svg>
);

export const BarsIcon = ({ size = 20, ...p }: P) => (
  <svg {...base(size, p)} stroke="#0369a1" strokeWidth={1.7}>
    <rect x="4" y="12" width="3.5" height="8" rx="1" fill="#0369a1" fillOpacity=".18" />
    <rect x="10.25" y="8" width="3.5" height="12" rx="1" fill="#0369a1" fillOpacity=".18" />
    <rect x="16.5" y="4" width="3.5" height="16" rx="1" fill="#0369a1" fillOpacity=".35" />
    <path d="M3 21h18" />
  </svg>
);

export const CloudIcon = ({ size = 20, ...p }: P) => (
  <svg {...base(size, p)} stroke="#0369a1" strokeWidth={1.7}>
    <path
      d="M7 18.5h10.5a4 4 0 0 0 .6-7.95A6 6 0 0 0 6.6 9.2 4.7 4.7 0 0 0 7 18.5z"
      fill="#0369a1"
      fillOpacity=".14"
    />
    <path d="M12 10.5v5.5M9.75 13.75 12 16l2.25-2.25" />
  </svg>
);

export const LockIcon = ({ size = 16, ...p }: P) => (
  <svg {...base(size, p)} stroke="#94a3b8" strokeWidth={2}>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

export const CameraIcon = ({ size = 24, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={1.8}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

/** Face ID glyph: corner brackets around a simple face. */
export const FaceIdIcon = ({ size = 18, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={2}>
    <path d="M3 8V5a2 2 0 0 1 2-2h3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M8 21H5a2 2 0 0 1-2-2v-3" />
    <path d="M9 9v1.5M15 9v1.5M12 9v4h-1M9 16a4.2 4.2 0 0 0 6 0" />
  </svg>
);

export const FingerprintIcon = ({ size = 18, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={1.9}>
    <path d="M12 11v3.5a8 8 0 0 1-1.3 4.4M8.5 7.5A5 5 0 0 1 17 11v2.5M7 11a5 5 0 0 1 .3-1.7M17 17.5c-.3.9-.7 1.8-1.2 2.5" />
    <path d="M4.5 9A8 8 0 0 1 19.6 8.5M4 13v-1M20 12.5v1M9 12v2a10 10 0 0 1-1.2 4.8" />
  </svg>
);

/** A tablet on a stand: front-desk devices. */
export const DeskIcon = ({ size = 18, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={2}>
    <rect x="4" y="3" width="16" height="12" rx="2" />
    <path d="M12 15v4M8 21h8" />
  </svg>
);

export const ReplayIcon = ({ size = 15, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={2.2}>
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

export const PrintIcon = ({ size = 18, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={2}>
    <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" rx="1" />
  </svg>
);

export const KeyIcon = ({ size = 18, ...p }: P) => (
  <svg {...base(size, p)} strokeWidth={2}>
    <circle cx="7.5" cy="15.5" r="4.5" />
    <path d="m10.7 12.3 9.8-9.8M17 6l3 3M14 9l2 2" />
  </svg>
);

/** Bottom-nav icons: filled tint when active (design's `icon(kind, on)`). */
export function NavIcon({ kind, on }: { kind: 'hub' | 'tl' | 'stu' | 'me'; on: boolean }) {
  const t = on ? { fill: 'currentColor', fillOpacity: 0.16 } : { fill: 'none' };
  const solid = { fill: on ? 'currentColor' : '#fff' };
  const svgProps = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: on ? 2 : 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (kind === 'hub')
    return (
      <svg {...svgProps}>
        <circle cx={12} cy={12} r={8.75} {...t} />
        <path d="M12 7.25a4.75 4.75 0 1 1-4.75 4.75" />
        <circle cx={12} cy={12} r={1.3} fill="currentColor" stroke="none" />
      </svg>
    );
  if (kind === 'tl')
    return (
      <svg {...svgProps}>
        <path d="M6.5 3.5v17" />
        <circle cx={6.5} cy={7.5} r={2} {...solid} />
        <circle cx={6.5} cy={16.5} r={2} {...solid} />
        <rect x={11} y={5} width={9.5} height={5} rx={1.8} {...t} />
        <rect x={11} y={14} width={9.5} height={5} rx={1.8} {...t} />
      </svg>
    );
  if (kind === 'stu')
    return (
      <svg {...svgProps}>
        <circle cx={9} cy={8} r={3.5} {...t} />
        <path d="M2.75 20a6.25 6.25 0 0 1 12.5 0z" {...t} />
        <path d="M15.5 4.75a3.5 3.5 0 0 1 0 6.5M18 14.5a6.25 6.25 0 0 1 3.25 5.5" />
      </svg>
    );
  return (
    <svg {...svgProps}>
      <circle cx={12} cy={12} r={9} {...t} />
      <circle cx={12} cy={10} r={3} />
      <path d="M6.6 18.4a6.5 6.5 0 0 1 10.8 0" />
    </svg>
  );
}
